import { Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { globalClasses } from 'core/common/constants/global-classes';
import { CheckboxVariant } from 'core/flow/entities';
import { pointerHoverMediaQuery } from '../constants';

declare module '@mui/material/FormControlLabel' {
  interface FormControlLabelPropsVariantOverrides {
    option: true;
    image: true;
    card: true;
  }

  interface FormControlLabelProps {
    variant?: CheckboxVariant;
  }
}

type FormControlLabelOwnerState = {
  disabled: boolean;
  variant?: CheckboxVariant;
  checked: boolean;
};

const getCheckboxVariantSharedStyles = ({ palette, breakpoints }: Theme) => ({
  marginLeft: 0,
  marginRight: 0,
  color: palette.button.contrastOptionText,
  borderRadius: '8px',
  textTransform: 'none',
  display: 'flex',
  justifyContent: 'flex-start',
  minHeight: '56px',
  padding: '16px',
  height: '100%',
  backgroundPosition: 'center center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  transition: 'all 0.2s ease-in-out',
  [pointerHoverMediaQuery]: {
    '&:hover': {
      border: `1px solid ${palette.button.optionHover}`,
      color: palette.button.contrastOptionText,
      backgroundColor: alpha(palette.button.optionHover, 0.2),
    },
  },
  '&:disabled': {
    backgroundColor: palette.button.optionDisabled,
    color: palette.button.contrastOptionText,
  },
  [`& ${globalClasses.typographyRoot}`]: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  [`& ${globalClasses.checkboxRoot}`]: {
    padding: 0,
  },
  [`& ${globalClasses.svgIconRoot}`]: {
    width: '24px',
    height: '24px',
  },
  [`& ${globalClasses.boxRoot}`]: {
    fontSize: '16px',
    [breakpoints.up('mobile')]: {
      fontSize: '18px',
    },
  },
});

const imageVariantBorderRadiusPx = 12;

const getBorderColor = (palette: Theme['palette'], ownerState: FormControlLabelOwnerState) => {
  const isImage = ownerState.variant === CheckboxVariant.IMAGE;

  if (ownerState.checked) {
    return isImage ? palette.button.optionHover : palette.button.optionIcon;
  }

  return isImage ? 'transparent' : palette.button.optionBorder;
};

const getImageVariantDynamicStyles = (
  palette: Theme['palette'],
  ownerState: FormControlLabelOwnerState,
) => ({
  backgroundColor: palette.button.option,
  backgroundClip: 'padding-box',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: `${imageVariantBorderRadiusPx}px`,
    backgroundColor: alpha(palette.button.optionHover, 0.2),
    opacity: ownerState.checked ? 1 : 0,
    transition: 'opacity 0.2s ease-in-out',
    pointerEvents: 'none',
  },
});

const cardTileBorderRadiusPx = 8;

const getCardVariantDynamicStyles = (
  palette: Theme['palette'],
  ownerState: FormControlLabelOwnerState,
) => ({
  opacity: ownerState.disabled ? 0.5 : 1,
  [`& ${globalClasses.checkboxRoot}`]: {
    backgroundColor: ownerState.checked
      ? alpha(palette.button.optionHover, 0.2)
      : palette.button.option,
    borderColor: ownerState.checked ? palette.button.optionHover : palette.button.optionBorder,
  },
});

const getDynamicVariantStyles = (
  palette: Theme['palette'],
  ownerState: FormControlLabelOwnerState,
) => ({
  opacity: ownerState.disabled ? 0.5 : 1,
  backgroundColor: ownerState.checked
    ? alpha(palette.button.optionHover, 0.2)
    : palette.button.option,
  border: `1px solid ${getBorderColor(palette, ownerState)}`,
  ...(ownerState.variant === CheckboxVariant.IMAGE &&
    getImageVariantDynamicStyles(palette, ownerState)),
});

export const getFormControlLabelOptions = (theme: Theme) => {
  const { palette } = theme;
  const sharedStyles = getCheckboxVariantSharedStyles(theme);

  const formControlLabelComponents = {
    styleOverrides: {
      root: ({ ownerState }: { ownerState: FormControlLabelOwnerState }) => {
        if (!ownerState.variant) {
          return {
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          };
        }

        if (ownerState.variant === CheckboxVariant.CARD) {
          return getCardVariantDynamicStyles(palette, ownerState);
        }

        return getDynamicVariantStyles(palette, ownerState);
      },
    },
    variants: [
      {
        props: { variant: CheckboxVariant.IMAGE },
        style: {
          ...sharedStyles,
          position: 'relative',
          flexDirection: 'row',
          alignItems: 'flex-start',
          height: '96px',
          marginBottom: '4px',
          overflow: 'hidden',
          borderRadius: `${imageVariantBorderRadiusPx}px`,
          [`& ${globalClasses.typographyRoot}`]: {
            marginBottom: '16px',
          },
          [`& ${globalClasses.checkboxRoot}`]: {
            position: 'absolute',
            top: '7px',
            right: '7px',
            zIndex: 1,
            padding: 0,
          },
          img: {
            width: '44px',
            height: '44px',
            marginBottom: '8px',
          },
        },
      },
      {
        props: { variant: CheckboxVariant.OPTION },
        style: {
          ...sharedStyles,
          alignItems: 'center',
          [`& svg:not(${globalClasses.svgIconRoot})`]: {
            color: palette.button.optionIcon,
          },
        },
      },
      {
        props: { variant: CheckboxVariant.CARD },
        style: {
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          margin: 0,
          color: palette.button.contrastOptionText,
          textAlign: 'center',
          [`& ${globalClasses.checkboxRoot}`]: {
            width: '100%',
            height: 'auto',
            aspectRatio: '1',
            padding: 0,
            border: '1px solid',
            borderRadius: `${cardTileBorderRadiusPx}px`,
            transition: 'all 0.2s ease-in-out',
            [pointerHoverMediaQuery]: {
              '&:hover': {
                borderColor: palette.button.optionHover,
                backgroundColor: alpha(palette.button.optionHover, 0.2),
              },
            },
          },
        },
      },
    ],
  };

  return { formControlLabelComponents };
};
